import React, { useState, useCallback, useMemo, useContext } from "react";
import styled from "styled-components";
import { DrilldownList, SearchActionsToolbar } from "@/components/UI/DrilldownList";
import { LightboxContext } from "@/components/UI/lightbox";
import { useInFlightToggle } from "@/utils/customHooks/useInFlightToggle";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";

const StyledOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
`;

const StyledPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: auto;
  margin-top: 4px;
  ${globalScrollbarStyling}
`;

/**
 * Two-level drilldown panel for staticType filters whose options are grouped
 * by content type/dataset (currently the Document Field filter).
 *
 * Top level = dataset folders, second level = field options under that dataset.
 * Selection is by leaf value; the same valuePath that exists across multiple
 * datasets stays in sync visually because selection is keyed by `value`.
 */
export const GroupedStaticOptionsPanel = ({ filterConfig, groupedOptions, value, onChange }) => {
  // Close the surrounding lightbox (the one opened by FilterListItem) so the
  // level-1 toolbar can show an X close button matching level-2's behavior,
  // which is auto-injected by DrilldownLevel for non-root headers.
  const lightboxCtx = useContext(LightboxContext);
  const handleClose = useCallback(() => lightboxCtx?.setOpen(false), [lightboxCtx]);

  // Level-1 controls operate on dataset rows.
  const [groupSearch, setGroupSearch] = useState("");
  const [groupSortOrder, setGroupSortOrder] = useState(null);
  const [groupShowSelectedFirst, setGroupShowSelectedFirst] = useState(false);

  // Level-2 controls are per-dataset, keyed by contentType.
  // Each entry: { search, sortOrder, showSelectedFirst }.
  const [childStateMap, setChildStateMap] = useState({});
  const getChildState = useCallback(
    (ct) =>
      childStateMap[ct] || { search: "", sortOrder: null, showSelectedFirst: false },
    [childStateMap]
  );
  const patchChildState = useCallback(
    (ct, patch) =>
      setChildStateMap((prev) => ({
        ...prev,
        [ct]: { ...(prev[ct] || { search: "", sortOrder: null, showSelectedFirst: false }), ...patch },
      })),
    []
  );

  const selectedValueSet = useMemo(
    () => new Set((value || []).map((v) => v.value)),
    [value]
  );

  // Level 1 — filter & sort dataset rows by group display name only.
  const visibleGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    let out = q
      ? groupedOptions.filter((g) => (g.display || "").toLowerCase().includes(q))
      : [...groupedOptions];

    if (groupSortOrder) {
      out = [...out].sort((a, b) =>
        groupSortOrder === "asc"
          ? (a.display || "").localeCompare(b.display || "")
          : (b.display || "").localeCompare(a.display || "")
      );
    }

    if (groupShowSelectedFirst) {
      const isFullySelected = (g) =>
        g.children.length > 0 &&
        g.children.every((c) => selectedValueSet.has(c.value));
      out = [...out].sort(
        (a, b) => (isFullySelected(a) ? 0 : 1) - (isFullySelected(b) ? 0 : 1)
      );
    }
    return out;
  }, [groupedOptions, groupSearch, groupSortOrder, groupShowSelectedFirst, selectedValueSet]);

  const toggle = useInFlightToggle();

  const handleLeafChange = useCallback(
    (option) => {
      const newValues = toggle({
        key: "_",
        fallbackValues: value,
        option,
        buildEntry: (o, id) => ({ value: id, display: o.display }),
      });
      onChange(newValues);
    },
    [value, onChange, toggle]
  );

  // ---- Level-1 toolbar handlers ----
  // selectAll/clearAll/invert at level 1 act on every leaf across all groups
  // (after the level-1 search filter), so the toolbar feels global.
  const allLeafOptionsAcrossVisibleGroups = useMemo(() => {
    const seen = new Set();
    const out = [];
    visibleGroups.forEach((g) =>
      g.children.forEach((c) => {
        if (seen.has(c.value)) return;
        seen.add(c.value);
        out.push({ value: c.value, display: c.display });
      })
    );
    return out;
  }, [visibleGroups]);

  const handleGroupSearch = useCallback((v) => setGroupSearch(v), []);
  
  // Select-all/Clear-all act on the currently-visible (search-filtered) leaves while
  // preserving the selection state of leaves outside the current search.
  const handleGroupSelectAll = useCallback(() => {
    const existing = new Set((value || []).map((v) => v.value));
    const additions = allLeafOptionsAcrossVisibleGroups.filter((o) => !existing.has(o.value));
    onChange([...(value || []), ...additions]);
  }, [allLeafOptionsAcrossVisibleGroups, value, onChange]);

  const handleGroupClearAll = useCallback(() => {
    const leafKeys = new Set(allLeafOptionsAcrossVisibleGroups.map((o) => o.value));
    onChange((value || []).filter((v) => !leafKeys.has(v.value)));
  }, [allLeafOptionsAcrossVisibleGroups, value, onChange]);

  const handleGroupInvert = useCallback(() => {
    const inverted = allLeafOptionsAcrossVisibleGroups.filter(
      (o) => !selectedValueSet.has(o.value)
    );
    onChange(inverted);
  }, [allLeafOptionsAcrossVisibleGroups, selectedValueSet, onChange]);
  const handleGroupSort = useCallback(
    () => setGroupSortOrder((p) => (p === "asc" ? "desc" : "asc")),
    []
  );
  const handleGroupShowSelectedFirst = useCallback(
    () => setGroupShowSelectedFirst((p) => !p),
    []
  );

  const drilldownValue = useMemo(
    () => (value || []).map((v) => ({ _id: v.value, display: v.display, value: v.value })),
    [value]
  );

  const topLevelOptions = useMemo(
    () =>
      visibleGroups.map((g) => ({
        _id: `__group__${g.contentType}`,
        value: `__group__${g.contentType}`,
        display: g.display,
        icon: "FolderSimple",
        hasChildren: true,
        __contentType: g.contentType,
        __groupChildren: g.children,
      })),
    [visibleGroups]
  );

  const topLevelValue = useMemo(
    () =>
      topLevelOptions
        .filter(
          (g) =>
            g.__groupChildren.length > 0 &&
            g.__groupChildren.every((c) => selectedValueSet.has(c.value))
        )
        .map((g) => ({ _id: g._id, value: g.value, display: g.display })),
    [topLevelOptions, selectedValueSet]
  );

  const handleGroupToggle = useCallback(
    (groupOption) => {
      const childValues = (groupOption.__groupChildren || []).map((c) => c.value);
      if (childValues.length === 0) return;
      const allSelected = childValues.every((v) => selectedValueSet.has(v));
      if (allSelected) {
        const childSet = new Set(childValues);
        onChange((value || []).filter((v) => !childSet.has(v.value)));
      } else {
        const merged = [...(value || [])];
        const existing = new Set(merged.map((v) => v.value));
        groupOption.__groupChildren.forEach((c) => {
          if (!existing.has(c.value)) merged.push({ value: c.value, display: c.display });
        });
        onChange(merged);
      }
    },
    [value, selectedValueSet, onChange]
  );

  // ---- Level-2 (per-dataset) ----
  const buildChildLevelHeader = useCallback(
    (contentType, visibleChildren) => {
      const cs = getChildState(contentType);
      const replaceVisible = (next) => {
        // Replace the slice of `value` corresponding to this dataset's
        // currently-visible (post-child-search) children with `next`.
        const visibleIds = new Set(visibleChildren.map((c) => c.value));
        const retained = (value || []).filter((v) => !visibleIds.has(v.value));
        onChange([...retained, ...next]);
      };
      return (
        <SearchActionsToolbar
          search={{
            value: cs.search,
            onChange: (v) => patchChildState(contentType, { search: v }),
          }}
          selectAll={{
            display: "Select all",
            icon: "CheckCircle",
            onClick: () =>
              replaceVisible(visibleChildren.map((c) => ({ value: c.value, display: c.display }))),
          }}
          clearAll={{
            display: "Clear all",
            icon: "XCircle",
            onClick: () => replaceVisible([]),
          }}
          invertSelection={{
            icon: "Shuffle",
            tooltip: "Invert selection",
            onClick: () => {
              const inverted = visibleChildren
                .filter((c) => !selectedValueSet.has(c.value))
                .map((c) => ({ value: c.value, display: c.display }));
              replaceVisible(inverted);
            },
          }}
          sort={{
            icon: "SortDescending",
            tooltip: cs.sortOrder === "desc" ? "Sort ascending" : "Sort descending",
            onClick: () =>
              patchChildState(contentType, {
                sortOrder: cs.sortOrder === "asc" ? "desc" : "asc",
              }),
          }}
          showSelectedFirst={{
            icon: "ListNumbers",
            active: cs.showSelectedFirst,
            tooltip: "Show selected first",
            onClick: () =>
              patchChildState(contentType, { showSelectedFirst: !cs.showSelectedFirst }),
          }}
        />
      );
    },
    [getChildState, patchChildState, value, onChange, selectedValueSet]
  );

  const getChildLevel = useCallback(
    (groupOption) => {
      const ct = groupOption.__contentType;
      const cs = getChildState(ct);
      const q = cs.search.trim().toLowerCase();

      let visibleChildren = q
        ? (groupOption.__groupChildren || []).filter((c) =>
            (c.display || "").toLowerCase().includes(q)
          )
        : groupOption.__groupChildren || [];

      let opts = visibleChildren.map((c) => ({
        _id: c.value,
        display: c.display,
        value: c.value,
        hasChildren: false,
      }));

      if (cs.sortOrder) {
        opts = [...opts].sort((a, b) =>
          cs.sortOrder === "asc"
            ? (a.display || "").localeCompare(b.display || "")
            : (b.display || "").localeCompare(a.display || "")
        );
      }
      if (cs.showSelectedFirst) {
        opts = [...opts].sort(
          (a, b) =>
            (selectedValueSet.has(a.value) ? 0 : 1) -
            (selectedValueSet.has(b.value) ? 0 : 1)
        );
      }

      return {
        options: opts,
        value: drilldownValue,
        isMulti: true,
        onChange: handleLeafChange,
        emptyMessage: cs.search ? "No results found" : "No options",
        showSelectedFirst: cs.showSelectedFirst,
        headerContent: buildChildLevelHeader(ct, visibleChildren),
      };
    },
    [getChildState, drilldownValue, handleLeafChange, selectedValueSet, buildChildLevelHeader]
  );

  return (
    <StyledOuterWrapper>
      <SearchActionsToolbar
        search={{ value: groupSearch, onChange: handleGroupSearch }}
        selectAll={{ display: "Select all", onClick: handleGroupSelectAll, icon: "CheckCircle" }}
        clearAll={{ display: "Clear all", onClick: handleGroupClearAll, icon: "XCircle" }}
        invertSelection={{
          onClick: handleGroupInvert,
          icon: "Shuffle",
          tooltip: "Invert selection",
        }}
        sort={{
          onClick: handleGroupSort,
          icon: "SortDescending",
          tooltip: groupSortOrder === "desc" ? "Sort ascending" : "Sort descending",
        }}
        showSelectedFirst={{
          onClick: handleGroupShowSelectedFirst,
          icon: "ListNumbers",
          active: groupShowSelectedFirst,
          tooltip: "Show selected first",
        }}
        onClose={lightboxCtx ? handleClose : undefined}
      />
      <StyledPanelWrapper>
        <DrilldownList
          options={topLevelOptions}
          value={topLevelValue}
          isMulti={true}
          onChange={handleGroupToggle}
          getChildLevel={getChildLevel}
          folderSelectionMode={"non-exclusive"}
          emptyMessage={groupSearch ? "No results found" : "No options available"}
        />
      </StyledPanelWrapper>
    </StyledOuterWrapper>
  );
};
